import { useContext, useCallback, useState, useRef } from 'react';
import { ChatContext } from '../contexts/ChatContext';
import { ChatService } from '@services/api';
import { Message } from '@/types';

export const useChat = () => {
    const context = useContext(ChatContext);

    const [streamingMessage, setStreamingMessage] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [currentTool, setCurrentTool] = useState<string | null>(null);

    // Use a ref to accumulate the message without state update issues
    const messageAccumulator = useRef('');

    if (!context) {
        throw new Error('useChat must be used within ChatProvider');
    }

    const chatService = ChatService.getInstance();

    const sendMessage = useCallback(async (content: string, hiddenContext?: string) => {
        if (!content.trim() || context.isLoading) return;

        // Create display message (what the user sees)
        const displayMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: content.trim(),
            timestamp: Date.now(),
            assistantId: context.selectedAssistant,
        };

        // Create API message (may include hidden context)
        const apiMessage: Message = {
            ...displayMessage,
            content: hiddenContext ? hiddenContext + content.trim() : content.trim(),
        };

        // Add the display message to the UI (without context)
        context.addMessage(displayMessage);
        context.setLoading(true);

        try {
            if (context.streamingEnabled) {
                // Reset the accumulator
                messageAccumulator.current = '';

                // Send API message (with context if provided)
                const messagesToSend = [...context.messages, apiMessage];

                // Set streaming state BEFORE starting the stream
                setIsStreaming(true);
                setStreamingMessage('');

                await chatService.streamMessage(
                    messagesToSend,
                    context.selectedAssistant,
                    (chunk) => {
                        // Clear tool status when content starts streaming
                        setCurrentTool(null);

                        // Accumulate in ref (always reliable)
                        messageAccumulator.current += chunk;

                        // Update state with functional update to ensure we don't lose chunks
                        setStreamingMessage(prev => prev + chunk);
                    },
                    (tool, status) => {
                        // Handle tool status updates
                        if (status === 'started') {
                            setCurrentTool(tool);
                        } else if (status === 'completed' || status === 'error') {
                            setCurrentTool(null);
                        }
                    }
                );

                // Use the ref value for the final message
                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: messageAccumulator.current,
                    timestamp: Date.now(),
                    assistantId: context.selectedAssistant,
                };

                context.addMessage(assistantMessage);
                context.setStreamingMessage('');

                // Clear the accumulator
                messageAccumulator.current = '';
            } else {
                const response = await chatService.sendMessage(
                    [...context.messages, apiMessage],
                    context.selectedAssistant
                );

                context.addMessage(response.message);
            }
        } catch (error) {
            console.error('Error in sendMessage:', error);
            context.setError(error as Error);

            // If there was a partial message, save it
            if (messageAccumulator.current) {
                const partialMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: messageAccumulator.current + '\n\n[Message interrupted due to error]',
                    timestamp: Date.now(),
                    assistantId: context.selectedAssistant,
                };
                context.addMessage(partialMessage);
            }
        } finally {
            // Reset everything
            setIsStreaming(false);
            setStreamingMessage('');
            setCurrentTool(null);
            messageAccumulator.current = '';
            context.setLoading(false);
        }
    }, [context, chatService]);

    return {
        ...context,
        streamingMessage,
        isStreaming,
        currentTool,
        sendMessage,
    };
};